import { derivePatternColor, normalizeHex } from './colorUtils';
export const DEFAULT_GRID_SIZE = 20;
export const DEFAULT_CANVAS_WIDTH = 1200;
export const DEFAULT_CANVAS_HEIGHT = 800;
/**
 * Shortest side that still fits ops labels (name, guests, order, total,
 * seated time, server initials) without clipping at normal zoom.
 * Max is large enough for banquet tables on the default 1200×800 hall.
 * Keep in sync with POS client `floor-plan/defaults.ts`.
 */
export const MIN_TABLE_SIZE = 64;
export const MAX_TABLE_SIZE = 720;
export const MIN_CANVAS_WIDTH = 480;
export const MIN_CANVAS_HEIGHT = 360;
export const MAX_CANVAS_WIDTH = 2400;
export const MAX_CANVAS_HEIGHT = 1800;
/** Quick hall/section floor sizes for the toolbar. */
export const SECTION_SIZE_PRESETS = [
    { id: 'S', label: 'S', width: 800, height: 600 },
    { id: 'M', label: 'M', width: 1200, height: 800 },
    { id: 'L', label: 'L', width: 1600, height: 1000 },
    { id: 'XL', label: 'XL', width: 2000, height: 1200 },
];
export const CANVAS_SIZE_STEP = 40;
export const HALL_BACKGROUND_PRESETS = [
    'light',
    'wood',
    'tile',
    'concrete',
    'dark',
];
export const HALL_BACKGROUNDS = [
    { id: 'light', label: 'Light', color: '#F5F5F2', patternColor: '#E8E8E2' },
    { id: 'wood', label: 'Wood', color: '#E8D5B7', patternColor: '#D4C0A0' },
    { id: 'tile', label: 'Tile', color: '#E2E8F0', patternColor: '#CBD5E1' },
    {
        id: 'concrete',
        label: 'Concrete',
        color: '#D1D5DB',
        patternColor: '#9CA3AF',
    },
    { id: 'dark', label: 'Dark', color: '#374151', patternColor: '#4B5563' },
];
export const DEFAULT_HALL_COLOR = '#F5F5F2';
export const TABLE_SHAPE_PRESETS = [
    { shape: 'square', label: 'Square', capacity: 2, width: 120, height: 120 },
    { shape: 'square', label: 'Square', capacity: 4, width: 120, height: 120 },
    { shape: 'square', label: 'Square', capacity: 6, width: 140, height: 140 },
    { shape: 'round', label: 'Round', capacity: 2, width: 120, height: 120 },
    { shape: 'round', label: 'Round', capacity: 4, width: 130, height: 130 },
    { shape: 'round', label: 'Round', capacity: 6, width: 150, height: 150 },
    { shape: 'round', label: 'Round', capacity: 8, width: 160, height: 160 },
    {
        shape: 'rectangle',
        label: 'Rectangle',
        capacity: 4,
        width: 160,
        height: 120,
    },
    {
        shape: 'rectangle',
        label: 'Rectangle',
        capacity: 6,
        width: 180,
        height: 120,
    },
    {
        shape: 'rectangle',
        label: 'Rectangle',
        capacity: 8,
        width: 220,
        height: 120,
    },
    { shape: 'booth', label: 'Booth', capacity: 4, width: 160, height: 120 },
    { shape: 'booth', label: 'Booth', capacity: 6, width: 200, height: 120 },
    { shape: 'booth', label: 'Booth', capacity: 8, width: 240, height: 130 },
    { shape: 'bar', label: 'Bar', capacity: 2, width: 160, height: 120 },
    { shape: 'bar', label: 'Bar', capacity: 4, width: 200, height: 120 },
    { shape: 'bar', label: 'Bar', capacity: 6, width: 260, height: 120 },
];
/** Pick a shape preset for a catalog table when seeding from DB capacity. */
export const resolvePresetForCapacity = (capacity) => {
    const seats = Math.max(1, Math.round(Number(capacity) || 4));
    const exact = TABLE_SHAPE_PRESETS.find(preset => preset.capacity === seats);
    if (exact) {
        return { ...exact, capacity: seats };
    }
    const nearest = TABLE_SHAPE_PRESETS.find(preset => preset.capacity >= seats) ||
        TABLE_SHAPE_PRESETS[TABLE_SHAPE_PRESETS.length - 1];
    return { ...nearest, capacity: seats };
};
/** Quick size steps for inspector UI (not typed). */
export const TABLE_SIZE_PRESETS = [
    { id: 'S', label: 'S', scale: 0.75 },
    { id: 'M', label: 'M', scale: 1 },
    { id: 'L', label: 'L', scale: 1.25 },
    { id: 'XL', label: 'XL', scale: 1.5 },
];
export const SIZE_STEP = 20;
/** Default fill for builder tables when no custom color is set. */
export const DEFAULT_TABLE_COLOR = '#F8FAFC';
export const RECT_OUTLINE = [
    { x: 0, y: 0 },
    { x: 1, y: 0 },
    { x: 1, y: 1 },
    { x: 0, y: 1 },
];
/** Hall footprint presets (normalized 0–1). */
export const SECTION_SHAPE_PRESETS = [
    {
        id: 'rect',
        label: 'Rectangle',
        tip: 'Use a full rectangular hall',
        outline: RECT_OUTLINE,
    },
    {
        id: 'l',
        label: 'L shape',
        tip: 'Use an L-shaped hall',
        outline: [
            { x: 0, y: 0 },
            { x: 0.58, y: 0 },
            { x: 0.58, y: 0.48 },
            { x: 1, y: 0.48 },
            { x: 1, y: 1 },
            { x: 0, y: 1 },
        ],
    },
    {
        id: 'u',
        label: 'U shape',
        tip: 'Use a U-shaped hall',
        outline: [
            { x: 0, y: 0 },
            { x: 1, y: 0 },
            { x: 1, y: 1 },
            { x: 0.72, y: 1 },
            { x: 0.72, y: 0.42 },
            { x: 0.28, y: 0.42 },
            { x: 0.28, y: 1 },
            { x: 0, y: 1 },
        ],
    },
    {
        id: 't',
        label: 'T shape',
        tip: 'Use a T-shaped hall',
        outline: [
            { x: 0, y: 0 },
            { x: 1, y: 0 },
            { x: 1, y: 0.4 },
            { x: 0.68, y: 0.4 },
            { x: 0.68, y: 1 },
            { x: 0.32, y: 1 },
            { x: 0.32, y: 0.4 },
            { x: 0, y: 0.4 },
        ],
    },
];
export const createDefaultSectionStyle = () => ({
    background: 'light',
    showGrid: true,
    gridSize: DEFAULT_GRID_SIZE,
});
export const createDefaultSectionLayout = () => ({
    style: createDefaultSectionStyle(),
    canvas: { width: DEFAULT_CANVAS_WIDTH, height: DEFAULT_CANVAS_HEIGHT },
    outline: RECT_OUTLINE.map(point => ({ ...point })),
    tables: {},
    objects: {},
});
export const normalizeOutline = (points) => {
    if (!points || points.length < 3) {
        return RECT_OUTLINE.map(point => ({ ...point }));
    }
    return points.map(point => ({
        x: Math.min(1, Math.max(0, point.x)),
        y: Math.min(1, Math.max(0, point.y)),
    }));
};
export const outlineToPixels = (outline, width, height) => normalizeOutline(outline).map(point => ({
    x: point.x * width,
    y: point.y * height,
}));
export const pixelsToOutline = (points, width, height) => points.map(point => ({
    x: width > 0 ? Math.min(1, Math.max(0, point.x / width)) : 0,
    y: height > 0 ? Math.min(1, Math.max(0, point.y / height)) : 0,
}));
export const outlinesEqual = (a, b) => {
    const left = normalizeOutline(a);
    const right = normalizeOutline(b);
    if (left.length !== right.length) {
        return false;
    }
    return left.every((point, index) => Math.abs(point.x - right[index].x) < 0.001 &&
        Math.abs(point.y - right[index].y) < 0.001);
};
export const createEmptyFloorPlan = () => ({
    version: 1,
    sections: {},
});
export const clampTableSize = (value) => Math.min(MAX_TABLE_SIZE, Math.max(MIN_TABLE_SIZE, value));
/** Bump undersized tables (e.g. legacy bar height 50) up to the readable minimum. */
export const ensureMinTablePlacement = (placement) => ({
    ...placement,
    width: clampTableSize(placement.width),
    height: clampTableSize(placement.height),
});
/**
 * When adding tables, shrink the shape preset to fit the current hall so a
 * dense pack still lands inside the footprint. Never grows above the preset;
 * never goes below MIN_TABLE_SIZE (LOD + zoom handle overflow on Dine-In).
 */
export const suggestTableSizeForHall = (presetWidth, presetHeight, canvasWidth, canvasHeight, tableCount, options) => {
    const n = Math.max(1, Math.round(tableCount));
    const padding = options?.padding ?? 48;
    const gap = options?.gap ?? 24;
    const areaW = Math.max(MIN_TABLE_SIZE, canvasWidth - padding * 2);
    const areaH = Math.max(MIN_TABLE_SIZE, canvasHeight - padding * 2);
    const aspect = areaW / Math.max(areaH, 1);
    const cols = Math.max(1, Math.ceil(Math.sqrt(n * aspect)));
    const rows = Math.max(1, Math.ceil(n / cols));
    const cellW = (areaW - gap * Math.max(0, cols - 1)) / cols;
    const cellH = (areaH - gap * Math.max(0, rows - 1)) / rows;
    const baseW = Math.max(1, presetWidth);
    const baseH = Math.max(1, presetHeight);
    const scale = Math.min(1, cellW / baseW, cellH / baseH);
    return {
        width: clampTableSize(Math.round(baseW * scale)),
        height: clampTableSize(Math.round(baseH * scale)),
    };
};
export const createDefaultTablePlacement = (shape, width, height, x = 40, y = 40, zIndex = 1) => ({
    x,
    y,
    width: clampTableSize(width),
    height: clampTableSize(height),
    rotation: 0,
    shape,
    zIndex,
    color: DEFAULT_TABLE_COLOR,
});
export const snapToGrid = (value, gridSize) => {
    if (gridSize <= 0) {
        return value;
    }
    return Math.round(value / gridSize) * gridSize;
};
export const getHallBackgroundColors = (background) => {
    const found = HALL_BACKGROUNDS.find(item => item.id === background);
    if (found) {
        return {
            color: found.color,
            patternColor: found.patternColor,
        };
    }
    const hex = normalizeHex(background || '');
    if (hex) {
        return {
            color: hex,
            patternColor: derivePatternColor(hex),
        };
    }
    return {
        color: DEFAULT_HALL_COLOR,
        patternColor: '#E8E8E2',
    };
};
export const resolveHallBackgroundHex = (background) => getHallBackgroundColors(background).color;
export const placementsOverlap = (a, b, padding = 4) => {
    return !(a.x + a.width + padding <= b.x ||
        b.x + b.width + padding <= a.x ||
        a.y + a.height + padding <= b.y ||
        b.y + b.height + padding <= a.y);
};
